import { gsap } from "gsap";
import { BLEND_MODES, Sprite, Texture } from "pixi.js";

import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { generateScaledSprite } from "../../../../abstracts/utils/GraphicUtils";
import { symbolConfig } from "../../../config/DDDConfig";
import {
    DDDHighSymbolNames,
    DDDLowSymbolNames,
    DDDSpecialSymbolNames,
    DDDSymbolNames,
    DDDSymbolSize,
} from "../../../types/DDDEnums";
import { symbolsResizeConfig } from "./DDDSymbolsResizeConfig";

export class DDDSymbol extends AbstractSymbol {
    protected mainSpineMask: Sprite;
    protected winBlinkSprite: Sprite;

    constructor(symbolName: string) {
        super(symbolName, DDDSymbolSize.X1);
    }

    protected get symbolHeight(): number {
        return symbolConfig.symbolHeight;
    }

    set size(value: number | DDDSymbolSize) {
        this._size = value > DDDSymbolSize.X2 ? DDDSymbolSize.X2 : value;
    }

    get size() {
        return this._size;
    }

    set isSpinning(value: boolean) {
        this._isSpinning = value;

        if (this.mainSpine && value) {
            this.mainSpine.reset();
            this.mainSpine.state.setAnimation(0, `Spin`, false);
        }
    }

    protected init(): void {
        this.generateSymbol();
    }

    protected generateSymbol(): void {
        if (
            Object.values(DDDLowSymbolNames).includes(this.name as DDDLowSymbolNames) ||
            Object.values(DDDHighSymbolNames).includes(this.name as DDDHighSymbolNames) ||
            this.name === DDDSpecialSymbolNames.WILD
        ) {
            const spineName: string = `spine/DDD/${this.name}.json`;

            this.mainSpine = new AbstractSpine(spineName);
            this.mainSpine.getSpineTween(`Normal`);

            this.mainSpine.x =
                symbolConfig.symbolWidth / 2 +
                symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetX!;
            this.mainSpine.y =
                symbolConfig.symbolHeight / 2 +
                symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetY!;
            this.addChild(this.mainSpine);
        } else {
            this.mainTextureSprite = new Sprite(Texture.from(`DDD/` + this.name));
            this.mainTextureSprite.name = `mainTextureSprite`;
            this.mainTextureSprite.scale.set(0.5);

            this.mainTextureSprite.x =
                symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
            this.mainTextureSprite.y =
                symbolConfig.symbolHeight / 2 - this.mainTextureSprite.height / 2;
            this.mainTextureSprite.alpha = 1;

            this.addChild(this.mainTextureSprite);
        }

        if (!this.name.includes(`XN`) && !this.name.includes(`B`)) {
            this.winBlinkSprite = generateScaledSprite(`DDD/${this.name}_glow`);
            this.winBlinkSprite.name = `winBlink`;
            this.winBlinkSprite.x = symbolConfig.symbolWidth / 2 - this.winBlinkSprite.width / 2;
            this.winBlinkSprite.y = symbolConfig.symbolHeight / 2 - this.winBlinkSprite.height / 2;
            this.winBlinkSprite.blendMode = BLEND_MODES.ADD;
        }
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.fromTo(
            this.winBlinkSprite,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.5,
                repeat: 1,
                yoyo: true,
                onStart: () => {
                    this.addChild(this.winBlinkSprite);
                    this.alpha = 1;
                },
                onComplete: () => {
                    this.removeChild(this.winBlinkSprite);
                },
            }
        );
    }

    public getNoWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.to(this, {
            alpha: 0.25,
            duration: 0.5,
        });
    }

    public getIdleAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.to(this, {
            alpha: 1,
            duration: 0.5,
        });
    }

    public override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.fromTo(
            this.winBlinkSprite,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.08,
                repeat: 1,
                yoyo: true,
                onStart: () => {
                    this.addChild(this.winBlinkSprite);
                },
                onComplete: () => {
                    this.removeChild(this.winBlinkSprite);
                },
            }
        );
    }

    public getMainSpine(): AbstractSpine {
        return this.mainSpine;
    }

    public resetOnStartSpin(): void {
        // @ts-ignore
        this.filters = null;

        this.resetMainSpine(true);
        if (this.mainSpine !== undefined) {
            this.mainSpine.tint = 0xffffff;
        }
        this.resetWinFrameSpine();

        if (this.mainTextureSprite) {
            this.mainTextureSprite.tint = 0xffffff;
            this.mainTextureSprite.alpha = 1;
            // @ts-ignore
            this.mainTextureSprite.filters = null;

            this.addChild(this.mainTextureSprite);
        }

        this.alpha = 1;
        this.size = this.initialSize;
        this.isWild = false;
        this.isBlocked = false;

        if (this.winBlinkSprite) {
            this.removeChild(this.winBlinkSprite);
            gsap.killTweensOf(this.winBlinkSprite);
        }

        this.visible = true;
    }

    public override reset(): void {
        // @ts-ignore
        this.filters = null;

        if (this.mainTextureSprite) {
            // @ts-ignore
            this.mainTextureSprite.filters = null;
            this.mainTextureSprite.scale.set(0.5);
            this.mainTextureSprite.x =
                symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
            this.mainTextureSprite.y =
                symbolConfig.symbolHeight / 2 - this.mainTextureSprite.height / 2;
            this.mainTextureSprite.mask = null;
        }

        if (this.mainSpineMask) {
            this.removeChild(this.mainSpineMask);
        }

        this.resetMainSpine();

        if (this.mainSpine !== undefined) {
            this.mainSpine.tint = 0xffffff;

            // @ts-ignore
            this.mainSpine.filters = null;
        }

        this.resetWinFrameSpine();

        if (this.mainTextureSprite) {
            this.mainTextureSprite.tint = 0xffffff;
            this.mainTextureSprite.alpha = 1;
            this.addChild(this.mainTextureSprite);
        }

        this.alpha = 1;

        this.scale.set(1);

        this.size = this.initialSize;

        this.isWild = false;

        this.isBlocked = false;

        this.visible = true;
    }

    protected override resetMainSpine(isSpin: boolean = false): void {
        if (this.mainSpine !== undefined) {
            this.mainSpine.reset();

            this.mainSpine.getSpineTween(`Normal`);

            if (!isSpin) {
                this.mainSpine.mask = null;
                this.mainSpine.scale.set(0.5);

                this.mainSpine.x =
                    symbolConfig.symbolWidth / 2 +
                    symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetX!;
                this.mainSpine.y =
                    symbolConfig.symbolHeight / 2 +
                    symbolsResizeConfig.get(this.name as DDDSymbolNames)!.default.offsetY!;
            }

            this.addChild(this.mainSpine);
        }
    }

    private resetWinFrameSpine() {}
}
